"use client";

import { c as _c } from "react/compiler-runtime";
import { Children, memo, useEffect, useMemo, useRef, type ComponentPropsWithoutRef, type ElementType, type ReactNode } from "react";
import Markdown, { type Components, type ExtraProps } from "react-markdown";
import rehypeExternalLinks from "rehype-external-links";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import type { PluggableList } from "unified";
import { useEmoji } from "@/contexts/EmojiContext";
import { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import { ApiDropReferencedNFT } from "@/generated/models/ApiDropReferencedNFT";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import { DropContentPartType, createMarkdownContentRenderers } from "./dropPartMarkdown/content";
import { highlightCodeElement } from "./dropPartMarkdown/highlight";
import { createLinkRenderer } from "./dropPartMarkdown/linkHandlers";
const BreakComponent = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <br />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const mergeClassNames = (...classes: Array<string | undefined>): string => classes.filter(Boolean).join(" ");
const headingClassName = "tw-text-iron-200 tw-break-words word-break";
type MarkdownRendererProps<T extends ElementType> = ComponentPropsWithoutRef<T> & ExtraProps & {
  children?: ReactNode | undefined;
  className?: string | undefined;
};
type MarkdownCodeProps = MarkdownRendererProps<"code"> & {
  inline?: boolean | undefined;
};
type MarkdownComponentsOptions = {
  customRenderer: (content: ReactNode | undefined) => ReactNode;
  renderParagraph: Components["p"];
  renderAnchor: Components["a"];
  renderImage: Components["img"];
};
const InlineCodeRenderer = t0 => {
  const $ = _c(14);
  let children;
  let className;
  let props;
  let style;
  if ($[0] !== t0) {
    ({
      children,
      className,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = style;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    style = $[4];
  }
  let t1;
  if ($[5] !== style) {
    t1 = {
      ...style,
      textOverflow: "unset"
    };
    $[5] = style;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== className) {
    t2 = mergeClassNames("tw-text-iron-200 tw-whitespace-pre-wrap tw-break-words", className);
    $[7] = className;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== children || $[10] !== props || $[11] !== t1 || $[12] !== t2) {
    t3 = <code {...props} style={t1} className={t2}>{children}</code>;
    $[9] = children;
    $[10] = props;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
const CodeBlockRenderer = t0 => {
  const $ = _c(22);
  let children;
  let className;
  let props;
  let style;
  if ($[0] !== t0) {
    ({
      children,
      className,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = style;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    style = $[4];
  }
  const codeRef = useRef(null);
  let t1;
  if ($[5] !== children) {
    t1 = Children.toArray(children).map(_temp).join("");
    $[5] = children;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const codeText = t1;
  let t2;
  if ($[7] !== className) {
    t2 = typeof className === "string" ? /language-([\w+-]+)/.exec(className) : null;
    $[7] = className;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const match = t2;
  const language = match?.[1] ?? null;
  let t3;
  let t4;
  if ($[9] !== codeText || $[10] !== language) {
    t3 = () => {
      if (globalThis.window === undefined) {
        return;
      }
      const element = codeRef.current;
      if (!element) {
        return;
      }
      if (!codeText || codeText.trim() === "") {
        return;
      }
      highlightCodeElement(element, language).catch(_temp2);
    };
    t4 = [codeText, language];
    $[9] = codeText;
    $[10] = language;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  if ($[13] !== style) {
    t5 = {
      ...style,
      textOverflow: "unset"
    };
    $[13] = style;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== className) {
    t6 = mergeClassNames("tw-text-iron-200 tw-whitespace-pre-wrap tw-break-words", className);
    $[15] = className;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== children || $[18] !== props || $[19] !== t5 || $[20] !== t6) {
    t7 = <code {...props} ref={codeRef} style={t5} className={t6}>{children}</code>;
    $[17] = children;
    $[18] = props;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
};
const CodeRenderer = t0 => {
  const $ = _c(6);
  let inline;
  let props;
  if ($[0] !== t0) {
    ({
      inline,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = inline;
    $[2] = props;
  } else {
    inline = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== inline || $[4] !== props) {
    t1 = inline ? <InlineCodeRenderer {...props} /> : <CodeBlockRenderer {...props} />;
    $[3] = inline;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
const createMarkdownComponents = ({
  customRenderer,
  renderParagraph,
  renderAnchor,
  renderImage
}: MarkdownComponentsOptions): Components => {
  const createHeadingRenderer = <T extends ElementType,>(Tag: T) => {
    const HeadingRenderer = t0 => {
      const $ = _c(14);
      let children;
      let className;
      let props;
      if ($[0] !== t0) {
        ({
          children,
          className,
          ...props
        } = t0);
        $[0] = t0;
        $[1] = children;
        $[2] = className;
        $[3] = props;
      } else {
        children = $[1];
        className = $[2];
        props = $[3];
      }
      const t1 = props as Record<string, unknown>;
      let t2;
      if ($[4] !== className) {
        t2 = mergeClassNames(headingClassName, className);
        $[4] = className;
        $[5] = t2;
      } else {
        t2 = $[5];
      }
      let t3;
      if ($[6] !== t1 || $[7] !== t2) {
        t3 = {
          ...t1,
          className: t2
        };
        $[6] = t1;
        $[7] = t2;
        $[8] = t3;
      } else {
        t3 = $[8];
      }
      const mergedProps = t3;
      const t4 = mergedProps as any;
      let t5;
      if ($[9] !== children) {
        t5 = customRenderer(children);
        $[9] = children;
        $[10] = t5;
      } else {
        t5 = $[10];
      }
      let t6;
      if ($[11] !== t4 || $[12] !== t5) {
        t6 = <Tag {...t4}>{t5}</Tag>;
        $[11] = t4;
        $[12] = t5;
        $[13] = t6;
      } else {
        t6 = $[13];
      }
      return t6;
    };
    HeadingRenderer.displayName = `MarkdownHeading(${typeof Tag === "string" ? Tag : "component"})`;
    return HeadingRenderer;
  };
  const ListItemRenderer = t0 => {
    const $ = _c(12);
    let children;
    let className;
    let props;
    if ($[0] !== t0) {
      ({
        children,
        className,
        ...props
      } = t0);
      $[0] = t0;
      $[1] = children;
      $[2] = className;
      $[3] = props;
    } else {
      children = $[1];
      className = $[2];
      props = $[3];
    }
    let t1;
    if ($[4] !== className) {
      t1 = mergeClassNames("tw-text-md tw-text-iron-200 tw-break-words word-break", className);
      $[4] = className;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== children) {
      t2 = customRenderer(children);
      $[6] = children;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== props || $[9] !== t1 || $[10] !== t2) {
      t3 = <li {...props} className={t1}>{t2}</li>;
      $[8] = props;
      $[9] = t1;
      $[10] = t2;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  };
  const BlockQuoteRenderer = t0 => {
    const $ = _c(12);
    let children;
    let className;
    let props;
    if ($[0] !== t0) {
      ({
        children,
        className,
        ...props
      } = t0);
      $[0] = t0;
      $[1] = children;
      $[2] = className;
      $[3] = props;
    } else {
      children = $[1];
      className = $[2];
      props = $[3];
    }
    let t1;
    if ($[4] !== className) {
      t1 = mergeClassNames("tw-text-iron-200 tw-break-words word-break tw-pl-4 tw-border-l-4 tw-border-l-iron-500 tw-border-solid tw-border-t-0 tw-border-r-0 tw-border-b-0", className);
      $[4] = className;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== children) {
      t2 = customRenderer(children);
      $[6] = children;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== props || $[9] !== t1 || $[10] !== t2) {
      t3 = <blockquote {...props} className={t1}>{t2}</blockquote>;
      $[8] = props;
      $[9] = t1;
      $[10] = t2;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  };
  return {
    h1: createHeadingRenderer("h1"),
    h2: createHeadingRenderer("h2"),
    h3: createHeadingRenderer("h3"),
    h4: createHeadingRenderer("h4"),
    h5: createHeadingRenderer("h5"),
    ...(renderParagraph ? {
      p: renderParagraph
    } : {}),
    li: ListItemRenderer,
    code: CodeRenderer,
    ...(renderAnchor ? {
      a: renderAnchor
    } : {}),
    ...(renderImage ? {
      img: renderImage
    } : {}),
    br: BreakComponent,
    blockquote: BlockQuoteRenderer
  } satisfies Components;
};
export interface DropPartMarkdownProps {
  readonly mentionedUsers: Array<ApiDropMentionedUser>;
  readonly referencedNfts: Array<ApiDropReferencedNFT>;
  readonly partContent: string | null;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly textSize?: "sm" | "md" | undefined;
  readonly currentDropId?: string | undefined;
}
function DropPartMarkdown(t0) {
  const $ = _c(24);
  const {
    mentionedUsers,
    referencedNfts,
    partContent,
    onQuoteClick,
    textSize,
    currentDropId
  } = t0;
  const isMobile = useIsMobileScreen();
  const {
    emojiMap,
    findNativeEmoji
  } = useEmoji();
  let t1;
  bb0: switch (textSize) {
    case "sm":
      {
        t1 = isMobile ? "tw-text-xs" : "tw-text-sm";
        break bb0;
      }
    default:
      {
        t1 = "tw-text-md";
      }
  }
  const textSizeClass = t1;
  let t2;
  if ($[0] !== currentDropId || $[1] !== onQuoteClick) {
    t2 = createLinkRenderer({
      onQuoteClick,
      currentDropId
    });
    $[0] = currentDropId;
    $[1] = onQuoteClick;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    renderAnchor,
    isSmartLink,
    renderImage
  } = t2;
  let t3;
  if ($[3] !== emojiMap || $[4] !== findNativeEmoji || $[5] !== isSmartLink || $[6] !== mentionedUsers || $[7] !== referencedNfts || $[8] !== textSizeClass) {
    t3 = createMarkdownContentRenderers({
      textSizeClass,
      mentionedUsers,
      referencedNfts,
      emojiMap,
      findNativeEmoji,
      isSmartLink
    });
    $[3] = emojiMap;
    $[4] = findNativeEmoji;
    $[5] = isSmartLink;
    $[6] = mentionedUsers;
    $[7] = referencedNfts;
    $[8] = textSizeClass;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    customRenderer,
    renderParagraph,
    processContent
  } = t3;
  let t4;
  if ($[10] !== partContent || $[11] !== processContent) {
    t4 = processContent(partContent);
    $[10] = partContent;
    $[11] = processContent;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const processedContent = t4;
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [rehypeExternalLinks, {
      target: "_blank",
      rel: ["noopener", "noreferrer", "nofollow"],
      protocols: ["http", "https"]
    }];
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [t5, [rehypeSanitize, {
      allowedTags: ["p", "br", "strong", "em", "a", "code", "pre", "blockquote", "h1", "h2", "h3", "h4", "h5", "h6", "ul", "ol", "li", "img"],
      allowedAttributes: {
        a: ["href", "title"],
        img: ["src", "alt", "title"],
        code: ["className"],
        pre: ["className"]
      }
    }]];
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const rehypePlugins = t6;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [remarkGfm];
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const remarkPlugins = t7;
  let t8;
  if ($[16] !== customRenderer || $[17] !== renderAnchor || $[18] !== renderImage || $[19] !== renderParagraph) {
    t8 = createMarkdownComponents({
      customRenderer,
      renderParagraph,
      renderAnchor,
      renderImage
    });
    $[16] = customRenderer;
    $[17] = renderAnchor;
    $[18] = renderImage;
    $[19] = renderParagraph;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const markdownComponents = t8;
  let t9;
  if ($[21] !== markdownComponents || $[22] !== processedContent) {
    t9 = <Markdown rehypePlugins={rehypePlugins} remarkPlugins={remarkPlugins} className="tw-w-full" components={markdownComponents}>{processedContent}</Markdown>;
    $[21] = markdownComponents;
    $[22] = processedContent;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}
export { DropContentPartType };
export default memo(DropPartMarkdown);
function _temp(child) {
  if (typeof child === "string" || typeof child === "number") {
    return child;
  }
  return "";
}
function _temp2(error) {
  console.error("[DropPartMarkdown] Failed to highlight code", error);
}